import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Trash2, UserPlus } from 'lucide-react';
import type { Role } from '@shared/protocol';
import { api } from '../lib/api';
import { useAuth, type User } from '../lib/auth';
import { useDocumentTitle } from '../lib/branding';
import { Notice } from '../components/Status';

type Listed = User & { createdAt: number; streamCount: number };

const DEFAULT_LIMIT = 3;

/** "" means no limit. */
const parseLimit = (v: string): number | null => (v.trim() === '' ? null : Math.max(0, Math.min(1000, Math.round(Number(v)))));

function RoleOptions() {
  return (
    <>
      <option value="user">User</option>
      <option value="operator">Operator</option>
      <option value="admin">Administrator</option>
    </>
  );
}

/** Stream limit for one User, saved when the field loses focus or Enter is pressed. */
function LimitField({ user, onSave }: { user: Listed; onSave: (limit: number | null) => void }) {
  const [value, setValue] = useState(user.streamLimit === null ? '' : String(user.streamLimit));
  useEffect(() => setValue(user.streamLimit === null ? '' : String(user.streamLimit)), [user.streamLimit]);
  const commit = () => {
    const next = parseLimit(value);
    if (next !== user.streamLimit && !Number.isNaN(next)) onSave(next);
  };
  return (
    <span className="limit-field">
      <input
        className="input input-inline input-narrow"
        type="number"
        min={0}
        max={1000}
        placeholder="None"
        value={value}
        aria-label={`Stream limit for ${user.email}`}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
      <span className="muted small">{user.streamCount} added</span>
    </span>
  );
}

export function UsersPage() {
  useDocumentTitle('Users');
  const { user: me } = useAuth();
  const [users, setUsers] = useState<Listed[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('user');
  const [limit, setLimit] = useState(String(DEFAULT_LIMIT));

  const load = useCallback(async () => {
    try {
      setUsers((await api<{ users: Listed[] }>('GET', '/api/users')).users);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load users.');
    }
  }, []);
  useEffect(() => void load(), [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The action failed.');
      return false;
    }
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const payload = { email, password, role, streamLimit: role === 'user' ? parseLimit(limit) : null };
    if (await act(() => api('POST', '/api/users', payload))) {
      setEmail('');
      setPassword('');
    }
  };

  const remove = (u: Listed) => {
    const streams = u.role === 'user' && u.streamCount > 0 ? ` Their ${u.streamCount === 1 ? 'stream' : `${u.streamCount} streams`}, with history and reports, will also be deleted.` : '';
    if (confirm(`Delete ${u.email}?${streams}`)) void act(() => api('DELETE', `/api/users/${u.id}`));
  };

  return (
    <div className="page narrow">
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <p className="muted">
            Administrators manage everything and see every stream. Operators monitor the shared streams. Users add and monitor their own streams, which only they and administrators can see.
          </p>
        </div>
      </div>
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Notice level="fail">{error}</Notice>
        </div>
      )}
      <div className="card" style={{ marginBottom: 24 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Stream limit</th>
              <th className="actions">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  {u.email}
                  {u.id === me?.id && <span className="muted small"> (you)</span>}
                </td>
                <td>
                  <select
                    className="input input-inline"
                    value={u.role}
                    aria-label={`Role for ${u.email}`}
                    onChange={(e) => void act(() => api('PATCH', `/api/users/${u.id}`, { role: e.target.value }))}
                  >
                    <RoleOptions />
                  </select>
                </td>
                <td>
                  {u.role === 'user' ? (
                    <LimitField user={u} onSave={(streamLimit) => void act(() => api('PATCH', `/api/users/${u.id}`, { streamLimit }))} />
                  ) : (
                    <span className="muted small">No limit</span>
                  )}
                </td>
                <td className="actions">
                  {u.id !== me?.id && (
                    <button type="button" className="btn btn-sm btn-ghost btn-danger" onClick={() => remove(u)}>
                      <Trash2 size={14} aria-hidden /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form className="card" onSubmit={add}>
        <header className="card-header">
          <h2>Add a user</h2>
        </header>
        <div className="card-body form-grid">
          <div className="field">
            <label htmlFor="u-email">Email address</label>
            <input id="u-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="u-password">Initial password</label>
            <input id="u-password" className="input" type="password" minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required />
            <span className="hint">At least 10 characters. Share it securely.</span>
          </div>
          <div className="field">
            <label htmlFor="u-role">Role</label>
            <select id="u-role" className="input" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <RoleOptions />
            </select>
          </div>
          {role === 'user' && (
            <div className="field">
              <label htmlFor="u-limit">Stream limit</label>
              <input id="u-limit" className="input" type="number" min={0} max={1000} placeholder="No limit" value={limit} onChange={(e) => setLimit(e.target.value)} />
              <span className="hint">Most streams this user can add. Leave empty for no limit.</span>
            </div>
          )}
        </div>
        <footer className="modal-footer">
          <button type="submit" className="btn btn-primary">
            <UserPlus size={16} aria-hidden /> Add user
          </button>
        </footer>
      </form>
    </div>
  );
}
